import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import type {PricedItem, PricingNodegraph} from "#pricing/types/pricing-types"
import type {PriceList} from "#pricing/nodes/price-list"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue, Version} from "@cm/graph-v3"
import {CircularRef, graphNode, parameterValue, versionChain} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type PricedItemNodeParametersV0 = {
    description: string //human-readable name, usually derived from the customer's catalog
    uniqueId: string //unique id, which is usually derived from the customer's catalog. This must map to a price.
    sku: string | undefined //sku maintained by the customer. Sometimes, this is the same as the uniqueId
    conditions: string[] | undefined //for now, this is supposed to be an array of configuration variant ids
    currentConfigurations: ConfigurationList | undefined //this will be populated from a set of ConfigurationGroupNodes, which serve as  interface to the configurator
    subPrices: PriceList | undefined //a list of related prices that depend on the current node
    conditionalPrice: boolean //if true, the price is only added, if the conditions are met. Allows to set up a conditional node that is not (yet) evaluated to make editing easier
    dependsOnSubprice: boolean //if true, the price is only added if a subprice is present. Can be used if the conditions on level of the pricing note are ambiguous, e.g. for darran's susu.
    amount?: number
}

export type PricedItemNodeParameters = {
    description: string //human-readable name, usually derived from the customer's catalog
    uniqueId: string //unique id, which is usually derived from the customer's catalog. This must map to a price.
    sku: string | undefined //sku maintained by the customer. Sometimes, this is the same as the uniqueId
    subPrices: PriceList | undefined //a list of related prices that depend on the current node
    dependsOnSubprice: boolean //if true, the price is only added if a subprice is present. Can be used if the conditions on level of the pricing note are ambiguous, e.g. for darran's susu.
    condition: ParameterValue<boolean, PricingContext> | undefined
    amount?: ParameterValue<number, PricingContext>
}

const v0: Version<PricedItemNodeParametersV0, PricedItemNodeParameters> = {
    toNextVersion: (parameters) => {
        return {
            description: parameters.description,
            uniqueId: parameters.uniqueId,
            sku: parameters.sku,
            condition: parameters.conditions
                ? (() => {
                      if (parameters.currentConfigurations instanceof CircularRef)
                          throw new Error("Cannot resolve circular references when going from v0 to v1 in priced item node")
                      return new VariantConditionNode({
                          parameters: {
                              condition: {variantIds: [...parameters.conditions], variantOperator: "AND", negated: false},
                              currentConfigurations: parameters.currentConfigurations ?? new ConfigurationList({parameters: {list: []}}),
                          },
                      })
                  })()
                : undefined,
            subPrices: parameters.subPrices,
            dependsOnSubprice: parameters.dependsOnSubprice,
            amount: parameters.amount,
        }
    },
}

const pricedItemNodeDefinition = {
    nodeClass: "PricedItemNode",
    validation: {
        parameters: z.object({
            description: z.string(),
            uniqueId: z.string(),
            sku: z.union([z.string(), z.undefined()]),
            subPrices: z.union([graphNode<PriceList>("PriceList"), z.undefined()]),
            dependsOnSubprice: z.boolean(),
            condition: z.union([parameterValue(z.boolean()), z.undefined()]),
            amount: z.optional(parameterValue(z.number())),
        }),
        returns: z.array(z.custom<PricedItem>(() => true)),
    },
    versionChain: versionChain([v0]),
} satisfies GraphNodeDefinition<PricedItem[], PricedItemNodeParameters>

@registerPricingGraphNode
export class PricedItemNode extends PricingNode<PricedItem[], PricedItemNodeParameters> {
    public override get definition(): GraphNodeDefinition<PricedItem[], PricedItemNodeParameters> {
        return pricedItemNodeDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        if (!this.parameters.dependsOnSubprice && this.parameters.condition) {
            const condition = await evaluator.get(this.parameters.condition)
            if (!condition) return []
        }

        //an amount of zero acts as a trigger as well: an item that is not part of the configuration must not show up in the price list.
        //this allows setting up amount-driven nodes (e.g. a ConditionalAmountGroupNode with defaultAmount 0) without a separate condition.
        const amount = (this.parameters.amount !== undefined ? await evaluator.get(this.parameters.amount) : undefined) ?? 1
        if (amount <= 0) return []

        const subPrices = this.parameters.subPrices ? (await evaluator.get(this.parameters.subPrices)).flat() : undefined
        const result = subPrices ? subPrices : []

        if (this.parameters.dependsOnSubprice && (subPrices === undefined || subPrices.length === 0)) return []

        result.unshift({
            description: this.parameters.description,
            price: evaluator.context.getPrice(this.parameters.uniqueId),
            currency: evaluator.context.getCurrency(),
            sku: this.parameters.sku,
            amount,
        })
        return result
    }

    /**The functions below are used for constructing the graph in a convenient way and for displaying it in the ui. They are not required for evaluating the graph.*/
    getUniqueId(): string {
        return this.parameters.uniqueId
    }

    getDescription(): string {
        return this.parameters.description
    }

    getPrice(context: PricingContext): number {
        return context.getPrice(this.parameters.uniqueId)
    }

    getSubprices(): PricingNodegraph[] {
        if (!this.parameters.subPrices) return []
        return this.parameters.subPrices.parameters.list
    }

    hasSubprices(): boolean {
        return this.getSubprices().length > 0
    }

    equals(node: PricedItemNode): boolean {
        if (!node) return false
        return this.parameters.uniqueId === node.parameters.uniqueId
    }

    addDependency(configGroupNode: ConfigurationGroupNode, variantId: string, variantName: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.addDependency(configGroupNode, variantId, variantName)
    }

    removeDependency(variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        this.parameters.condition.removeDependency(variantId)
    }

    canAddDependency(groupId: string, variantId: string) {
        if (!(this.parameters.condition instanceof VariantConditionNode)) throw new Error("Node does not accept conditions")
        return this.parameters.condition.canAddDependency(groupId, variantId)
    }

    getVariantIds(): string[] {
        if (!(this.parameters.condition instanceof VariantConditionNode)) return []
        return this.parameters.condition.getVariantIds()
    }
}
